import React, { useEffect, useState } from "react";
import { Flower2, Plus, Search, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge, PhotoUpload } from "@/components/common";
import { fileUrl } from "@/lib/api";
import { BarcodePrintModal, BarcodeInline, QrCode } from "@/components/BarcodePrint";

const CATS = [
  ["fresh", "Bunga Segar"], ["artificial", "Artificial"], ["raw_material", "Bahan Baku"],
  ["packaging", "Packaging"], ["accessories", "Aksesoris"], ["supplies", "Perlengkapan Toko"], ["other", "Lainnya"],
];
const CAT_LABEL = Object.fromEntries(CATS);

export default function Materials() {
  const { branch, can, isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState(null);
  const [printOpen, setPrintOpen] = useState(false);
  const canEdit = can("raw_materials", "edit") || isOwner;
  const canCreate = can("raw_materials", "create") || isOwner;

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (cat) params.category = cat;
      if (branch) params.branch_id = branch.id;
      setItems(await api.get("/materials", params));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cat, branch]);

  const filtered = items.filter((m) => !q || `${m.name} ${m.sku}`.toLowerCase().includes(q.toLowerCase()));

  const archive = async (m) => {
    const reason = window.prompt(`Arsipkan "${m.name}"? Alasan:`);
    if (!reason) return;
    try { await api.del(`/materials/${m.id}`, { reason }); toast.success("Diarsipkan"); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader
        title="Bahan & Perlengkapan"
        subtitle={branch?.name}
        icon={Flower2}
        actions={canCreate && <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPrintOpen(true)} data-testid="print-barcode-btn"><QrCode size={16} /> Cetak Barcode</Button>
          <Button onClick={() => setEditing({})} data-testid="add-material-btn"><Plus size={16} /> Item Baru</Button>
        </div>}
      />

      <Card className="p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari bahan..." className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" data-testid="material-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 px-3 rounded-xl border border-slate-300 text-sm">
          <option value="">Semua Kategori</option>
          {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Card>

      {loading ? <Loading /> : !filtered.length ? (
        <EmptyState icon={Flower2} title="Belum ada bahan" message="Tambahkan bahan atau perlengkapan." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 font-medium w-14"></th>
                  <th className="py-3 px-4 font-medium">Nama</th>
                  <th className="py-3 px-4 font-medium">Kategori</th>
                  <th className="py-3 px-4 font-medium text-right">Harga Beli</th>
                  {branch && <th className="py-3 px-4 font-medium text-right">Stok</th>}
                  <th className="py-3 px-4 font-medium text-right">Min</th>
                  {canEdit && <th className="py-3 px-4 font-medium text-right">Aksi</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`material-row-${m.id}`}>
                    <td className="py-2 px-4">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
                        {m.photo_file_id || m.image_url ? <img src={m.photo_file_id ? fileUrl(m.photo_file_id) : m.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-base">🌿</span>}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-800">{m.name}</div>
                      {m.sku && <div className="text-xs text-slate-400">{m.sku}</div>}
                    </td>
                    <td className="py-3 px-4"><Badge className="bg-slate-100 text-slate-600">{CAT_LABEL[m.category] || m.category}</Badge></td>
                    <td className="py-3 px-4 text-right text-slate-600">{rupiah(m.cost_price)} <span className="text-xs text-slate-400">/{m.unit}</span></td>
                    {branch && <td className={`py-3 px-4 text-right font-medium ${m.stock_qty <= m.min_stock ? "text-rose-600" : "text-slate-700"}`}>{num(m.stock_qty || 0)}</td>}
                    <td className="py-3 px-4 text-right text-slate-400">{num(m.min_stock)}</td>
                    {canEdit && (
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => setEditing(m)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><Pencil size={15} /></button>
                          <button onClick={() => archive(m)} className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && <MaterialModal item={editing} branch={branch} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <BarcodePrintModal open={printOpen} onClose={() => setPrintOpen(false)} title={cat === "supplies" ? "Cetak Barcode Perlengkapan" : "Cetak Barcode Bahan & Perlengkapan"} items={filtered.map((m) => ({ name: m.name, sku: m.sku }))} />
    </>
  );
}

function MaterialModal({ item, branch, onClose, onSaved }) {
  const isNew = !item.id;
  const [form, setForm] = useState({
    name: item.name || "", sku: item.sku || "", category: item.category || "fresh",
    unit: item.unit || "tangkai", cost_price: item.cost_price || 0, min_stock: item.min_stock || 0, location: item.location || "",
    purchase_unit: item.purchase_unit || "", conversion_factor: item.conversion_factor || "",
    photo_file_id: item.photo_file_id || null,
  });
  const [stockAdd, setStockAdd] = useState("");
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const genCode = async () => {
    if (!form.name.trim()) return toast.error("Isi nama item dulu sebelum generate kode");
    setGenBusy(true);
    try {
      const params = { name: form.name.trim() };
      if (item.id) params.exclude_id = item.id;
      const r = await api.get("/barcode/generate", params);
      set("sku", r.code);
    } catch (e) { toast.error(e.message); } finally { setGenBusy(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    if (form.purchase_unit && !(Number(form.conversion_factor) > 0)) return toast.error("Isi faktor konversi (contoh: 1 bundel = 20 tangkai)");
    setBusy(true);
    try {
      const body = { ...form, cost_price: Number(form.cost_price), min_stock: Number(form.min_stock), purchase_unit: form.purchase_unit || null, conversion_factor: form.purchase_unit ? Number(form.conversion_factor) : null };
      if (branch && Number(stockAdd) > 0) { body.stock_add = Number(stockAdd); body.stock_branch_id = branch.id; }
      if (isNew) {
        if (branch && Number(stockAdd) > 0) { body.stock_qty = Number(stockAdd); delete body.stock_add; }
        await api.post("/materials", body);
      } else await api.patch(`/materials/${item.id}`, body);
      toast.success(isNew ? "Bahan dibuat" : "Bahan diperbarui");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Item Baru" : "Edit Item"} maxWidth="max-w-md">
      <div className="space-y-3">
        <PhotoUpload value={form.photo_file_id} onChange={(id) => set("photo_file_id", id)} label="Foto Bahan / Perlengkapan" testid="mf-photo" />
        <Field label="Nama"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="minp" data-testid="mf-name" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU / Barcode">
            <div className="flex gap-2">
              <input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="minp flex-1" placeholder="Kode barcode" data-testid="mf-sku" />
              <button type="button" onClick={genCode} disabled={genBusy} className="shrink-0 h-10 px-3 rounded-xl border border-primary/40 bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 whitespace-nowrap disabled:opacity-60" data-testid="mf-gen-sku">{genBusy ? "..." : "Generate"}</button>
            </div>
          </Field>
          <Field label="Kategori">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="minp">
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        <BarcodeInline item={{ name: form.name, sku: form.sku }} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Satuan pakai (dasar)"><input value={form.unit} onChange={(e) => set("unit", e.target.value)} className="minp" placeholder="tangkai" /></Field>
          <Field label="Harga Beli / satuan dasar"><input type="number" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} className="minp" /></Field>
        </div>
        <div className="rounded-xl border border-slate-200 p-3 bg-slate-50/50">
          <div className="text-sm font-medium text-slate-600 mb-2">Satuan beli (opsional) — untuk beli per bundel</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nama satuan beli"><input value={form.purchase_unit} onChange={(e) => set("purchase_unit", e.target.value)} className="minp" placeholder="bundel / ikat" data-testid="mf-purchase-unit" /></Field>
            <Field label={`1 ${form.purchase_unit || "bundel"} = ? ${form.unit || "tangkai"}`}><input type="number" value={form.conversion_factor} onChange={(e) => set("conversion_factor", e.target.value)} className="minp" placeholder="20" data-testid="mf-conversion" /></Field>
          </div>
          {form.purchase_unit && Number(form.conversion_factor) > 0 && (
            <div className="text-xs text-emerald-600 mt-1">1 {form.purchase_unit} = {num(form.conversion_factor)} {form.unit} · HPP akan dihitung otomatis per {form.unit}.</div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Stok Minimal"><input type="number" value={form.min_stock} onChange={(e) => set("min_stock", e.target.value)} className="minp" /></Field>
          <Field label="Lokasi"><input value={form.location} onChange={(e) => set("location", e.target.value)} className="minp" /></Field>
        </div>
        {branch && (
          <Field label={`${isNew ? "Stok Awal" : "Tambah Stok"} di ${branch.name}`}>
            <input type="number" value={stockAdd} onChange={(e) => setStockAdd(e.target.value)} placeholder="0" className="minp" data-testid="mf-stock" />
          </Field>
        )}
        <Button className="w-full" onClick={save} disabled={busy} data-testid="mf-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
      <style>{`.minp{width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #cbd5e1;border-radius:.75rem;font-size:.875rem;outline:none}.minp:focus{border-color:hsl(var(--primary))}`}</style>
    </Modal>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">{label}</span>{children}</label>
);
